/**
 * Contrôle de la pause entre matières + application immédiate à l'EDT existant.
 * - Change la valeur enregistrée dans les contraintes
 * - Bouton "Appliquer maintenant" : recalcule creneaux_horaires + met à jour
 *   les heure_debut/heure_fin de tous les créneaux existants (les durées
 *   individuelles sont préservées).
 */
import React, { useState } from 'react';
import { Clock, RotateCcw } from 'lucide-react';
import {
  SessionExamen,
  useUpdateSessionMutation,
  useRegenererHorairesMutation,
} from '../../services/emploisTempsApi';

interface Props {
  session: SessionExamen;
  isDark: boolean;
  onDone: () => void;
}

const PRESETS = [0, 15, 30, 45, 60, 90, 120];

const PauseControl: React.FC<Props> = ({ session, isDark, onDone }) => {
  const [updateSession] = useUpdateSessionMutation();
  const [regenerer, { isLoading: applying }] = useRegenererHorairesMutation();
  const [heureDebut, setHeureDebut] = useState('08:00');
  const [dureeMatiere, setDureeMatiere] = useState(120);
  const [nbCreneaux, setNbCreneaux] = useState(3);

  const currentPause = session.contraintes?.duree_pause_minutes ?? 60;

  const changePause = async (n: number) => {
    // Applique IMMÉDIATEMENT la nouvelle pause aux créneaux existants.
    try {
      await updateSession({
        id: session.id,
        data: { contraintes: { duree_pause_minutes: n } },
      }).unwrap();
      const res: any = await regenerer({
        sessionId: session.id,
        duree_pause_minutes: n,
        duree_matiere_minutes: dureeMatiere,
        heure_debut: heureDebut,
        nb_creneaux: nbCreneaux,
        appliquer_aux_creneaux: true,
      }).unwrap();
      onDone();
      // Feedback : afficher le mapping ancien → nouveau pour rassurer l'utilisateur
      const mapping = res?.mapping || [];
      const nbUpdated = res?.creneaux_mis_a_jour ?? 0;
      const mappingTxt = mapping.length
        ? mapping.map((m: any) => `  ${m.old} → ${m.new}`).join('\n')
        : '(aucun créneau à mettre à jour)';
      alert(
        `Pause : ${n} min appliquée.\n\n` +
        `Nouveaux créneaux horaires :\n` +
        (res?.creneaux_horaires || []).map((s: any) => `  ${s.label}`).join('\n') +
        `\n\nCréneaux existants mis à jour : ${nbUpdated}\n${mappingTxt}\n\n` +
        (nbUpdated === 0 && mapping.length === 0
          ? "Aucun créneau existant n'a été trouvé — les nouvelles heures s'appliqueront à la prochaine génération."
          : ''),
      );
    } catch (err: any) {
      console.error(err);
      alert(err?.data?.error || "Erreur lors de l'application de la pause");
    }
  };

  const applyNow = async () => {
    if (!window.confirm(
      `Régénérer les créneaux horaires (pause ${currentPause} min, matière ${dureeMatiere} min) et appliquer aux créneaux existants ?\n\nLes durées individuelles des matières sont préservées.`,
    )) return;
    try {
      const res = await regenerer({
        sessionId: session.id,
        duree_pause_minutes: currentPause,
        duree_matiere_minutes: dureeMatiere,
        heure_debut: heureDebut,
        nb_creneaux: nbCreneaux,
        appliquer_aux_creneaux: true,
      }).unwrap();
      onDone();
      alert(`Créneaux horaires régénérés. ${res.creneaux_mis_a_jour} créneau(x) existant(s) mis à jour.`);
    } catch (err: any) {
      alert(err?.data?.error || "Erreur lors de l'application");
    }
  };

  return (
    <div className={`p-3 rounded-lg mb-3 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Clock size={16} className="text-indigo-500" />
        <div className="text-sm font-medium">Pause entre 2 matières consécutives</div>
      </div>
      <div className="text-xs text-gray-500 mb-2">
        Choisis la durée de pause — appliquée immédiatement aux créneaux existants
        (les durées individuelles des matières sont préservées).
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {PRESETS.map((n) => {
          const active = currentPause === n;
          return (
            <button
              key={n}
              onClick={() => changePause(n)}
              className={`px-2.5 py-1 text-xs rounded font-medium border ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : isDark ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {n} min
            </button>
          );
        })}
      </div>

      <div className={`p-3 rounded border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="text-xs font-medium mb-2">Appliquer maintenant à l'EDT existant</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">Heure de début</span>
            <input
              type="time"
              value={heureDebut}
              onChange={(e) => setHeureDebut(e.target.value)}
              className={`w-full px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
            />
          </label>
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">Durée matière (min)</span>
            <input
              type="number"
              min={30}
              max={480}
              value={dureeMatiere}
              onChange={(e) => setDureeMatiere(Math.max(30, Math.min(480, Number(e.target.value) || 120)))}
              className={`w-full px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
            />
          </label>
          <label className="text-xs">
            <span className="block text-gray-500 mb-1">Nb créneaux/jour</span>
            <input
              type="number"
              min={1}
              max={6}
              value={nbCreneaux}
              onChange={(e) => setNbCreneaux(Math.max(1, Math.min(6, Number(e.target.value) || 3)))}
              className={`w-full px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
            />
          </label>
        </div>
        <button
          onClick={applyNow}
          disabled={applying}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
        >
          <RotateCcw size={12} />
          {applying ? 'Application…' : 'Appliquer la pause aux créneaux existants'}
        </button>
        <div className="text-[11px] text-gray-500 mt-2">
          Recalcule les horaires (8H → 10H → pause → 11H → 13H, etc.) et met à jour les créneaux existants
          en conservant la durée de chaque matière.
        </div>
      </div>
    </div>
  );
};

export default PauseControl;
